import { useEffect, useState, useCallback } from 'react';
import {
  FiDollarSign,
  FiCalendar,
  FiMessageCircle,
  FiFileText,
  FiBell,
  FiMapPin,
  FiShield,
  FiTrendingDown,
} from 'react-icons/fi';
import { Button } from './Button';
import { Card } from './Card';
import { apiJson } from '@/lib/api';
import { track } from '@/lib/analytics';

// Legal URLs for subscription compliance
const TERMS_URL = 'https://harborcoparent.com/terms-of-service';
const PRIVACY_URL = 'https://harborcoparent.com/privacy-policy';
const CHECKOUT_INTERVAL_KEY = 'harbor_checkout_interval';
const YEARLY_TRIAL_DAYS = 7;

function trackCheckoutSuccess() {
  const interval = sessionStorage.getItem(CHECKOUT_INTERVAL_KEY);
  sessionStorage.removeItem(CHECKOUT_INTERVAL_KEY);
  const isAnnual = interval === 'year';
  track(isAnnual ? 'Trial Started' : 'Paywall Purchase Success', {
    package_type: isAnnual ? 'Annual' : 'Monthly',
    is_annual: isAnnual,
    is_trial: isAnnual,
  });
}

// Feature data with benefit-focused copy
const FEATURES = [
  {
    icon: FiDollarSign,
    text: 'Never argue about money again',
    description: 'Track and split expenses automatically',
  },
  {
    icon: FiCalendar,
    text: 'Know who has the kids, always',
    description: 'Visual custody calendar with exceptions',
  },
  {
    icon: FiMessageCircle,
    text: 'Every message timestamped and saved',
    description: 'Secure, documented communication',
  },
  {
    icon: FiFileText,
    text: 'Court-ready exports in one tap',
    description: 'Professional documentation when you need it',
  },
  {
    icon: FiMapPin,
    text: 'GPS check-ins for pickups & drop-offs',
    description: 'Log your location to document custody exchanges',
  },
  {
    icon: FiBell,
    text: 'Instant updates on changes',
    description: 'Push notifications keep you informed',
  },
];

type PaywallProps = {
  getToken: () => Promise<string | null>;
  userId?: number; // No longer used - backend gets user from auth token
  onPurchaseSuccess: () => void;
  userEmail?: string | null;
};

// Subscription plan from backend
type SubscriptionPlan = {
  price_id: string;
  name: string;
  amount_cents: number;
  currency: string;
  interval: string;
  interval_count: number;
};

type SubscriptionPlansResponse = {
  plans: SubscriptionPlan[];
};

type CheckoutSessionResponse = {
  checkout_url: string;
  session_id: string;
};

export function Paywall({
  getToken,
  onPurchaseSuccess,
  userEmail,
}: PaywallProps) {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  // Track screen view
  useEffect(() => {
    track('Paywall Screen');
  }, []);

  // Check for successful checkout return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutSuccess = urlParams.get('checkout_success');

    if (checkoutSuccess === 'true') {
      // Remove query param from URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      // Verify subscription with backend
      const verifySubscription = async () => {
        try {
          await apiJson(getToken, '/v1/subscription/verify', {
            method: 'POST',
          });
          trackCheckoutSuccess();
          onPurchaseSuccess();
        } catch (err) {
          console.error('[Paywall] Error verifying subscription:', err);
          // Still call success - webhook should have updated it
          trackCheckoutSuccess();
          onPurchaseSuccess();
        }
      };

      verifySubscription();
    }
  }, [getToken, onPurchaseSuccess]);

  // Fetch subscription plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        console.log('[Paywall] Fetching subscription plans...');
        const response = await apiJson<SubscriptionPlansResponse>(
          getToken,
          '/v1/subscription/plans'
        );

        console.log('[Paywall] Plans received:', response.plans);

        if (response.plans.length > 0) {
          setPlans(response.plans);
          // Select annual plan by default, or first plan
          const annualPlan = response.plans.find((p) => p.interval === 'year');
          setSelectedPlan(annualPlan || response.plans[0]);
        } else {
          setError(
            'Subscription options are not currently available. Please try again later.'
          );
        }

        setIsLoading(false);
      } catch (err) {
        console.error('[Paywall] Error fetching plans:', err);
        setError(
          'Unable to load subscription options. Please try again later.'
        );
        setIsLoading(false);
      }
    };

    fetchPlans();
  }, [getToken]);

  const handlePurchase = useCallback(async () => {
    if (!selectedPlan) {
      setError('Please select a subscription plan.');
      return;
    }

    const isAnnual = selectedPlan.interval === 'year';
    sessionStorage.setItem(CHECKOUT_INTERVAL_KEY, selectedPlan.interval);
    track('Paywall Purchase Tapped', {
      package_type: isAnnual ? 'Annual' : 'Monthly',
      is_annual: isAnnual,
      is_trial: isAnnual,
    });
    setIsPurchasing(true);
    setError(null);

    try {
      console.log(
        '[Paywall] Creating checkout session for:',
        selectedPlan.price_id
      );

      // Build success/cancel URLs
      const currentUrl = window.location.origin + window.location.pathname;
      const successUrl = `${currentUrl}?checkout_success=true`;
      const cancelUrl = currentUrl;

      const response = await apiJson<CheckoutSessionResponse>(
        getToken,
        '/v1/subscription/create-checkout-session',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            price_id: selectedPlan.price_id,
            success_url: successUrl,
            cancel_url: cancelUrl,
          }),
        }
      );

      console.log('[Paywall] Checkout session created:', response.session_id);

      // Redirect to Stripe Checkout
      window.location.href = response.checkout_url;
    } catch (err: unknown) {
      console.error('[Paywall] Checkout error:', err);
      const errorMessage = err instanceof Error ? err.message : 'An error occurred during checkout. Please try again.';
      setError(errorMessage);
      setIsPurchasing(false);
    }
  }, [selectedPlan, getToken]);

  const handleRestorePurchases = useCallback(async () => {
    track('Paywall Restore Tapped');
    setIsRestoring(true);
    setError(null);

    try {
      console.log('[Paywall] Verifying subscription...');

      await apiJson(getToken, '/v1/subscription/verify', {
        method: 'POST',
      });

      // If we get here, check if user is now subscribed
      // The verify endpoint returns the user, we could check subscribed status
      // For now, just call success and let the parent component refresh
      track('Paywall Restore Success');
      onPurchaseSuccess();
    } catch (err: unknown) {
      console.error('[Paywall] Restore error:', err);
      setError('No active subscription found for your account.');
    } finally {
      setIsRestoring(false);
    }
  }, [getToken, onPurchaseSuccess]);

  // Format price for display
  const formatPrice = (plan: SubscriptionPlan): string => {
    const amount = plan.amount_cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: plan.currency.toUpperCase(),
    }).format(amount);
  };

  // Get plan display info
  const getPlanInfo = (plan: SubscriptionPlan) => {
    const isAnnual = plan.interval === 'year';
    const priceString = formatPrice(plan);

    let monthlyEquivalent = '';
    let savingsPercent = 0;

    if (isAnnual) {
      const monthlyPrice = plan.amount_cents / 100 / 12;
      monthlyEquivalent =
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: plan.currency.toUpperCase(),
        }).format(monthlyPrice) + '/mo';

      // Calculate savings by comparing to monthly plan
      const monthlyPlan = plans.find((p) => p.interval === 'month');
      if (monthlyPlan) {
        const annualIfMonthly = monthlyPlan.amount_cents * 12;
        if (plan.amount_cents < annualIfMonthly) {
          savingsPercent = Math.round(
            ((annualIfMonthly - plan.amount_cents) / annualIfMonthly) * 100
          );
        }
      }
    }

    return {
      isAnnual,
      priceString,
      monthlyEquivalent,
      savingsPercent,
      title: isAnnual ? 'Annual' : 'Monthly',
      subtitle: isAnnual ? 'Best value' : 'Flexible',
    };
  };

  // Sort plans: annual first
  const sortedPlans = [...plans].sort((a, b) => {
    if (a.interval === 'year') return -1;
    if (b.interval === 'year') return 1;
    return 0;
  });

  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-primary via-[#026873] to-[#024950]'>
        <div className='text-center'>
          <div className='animate-spin h-8 w-8 border-2 border-white border-t-transparent rounded-full mx-auto' />
          <p className='mt-4 text-white/80 font-medium'>Loading options...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-sky-light'>
      {/* Hero Section with Gradient */}
      <div className='relative bg-gradient-to-br from-primary via-[#026873] to-[#024950] overflow-hidden'>
        {/* Wave Background Animation */}
        <div className='absolute inset-0 overflow-hidden pointer-events-none'>
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className='absolute left-[-50px] right-[-50px] h-20 bg-white/10 rounded-[100px] animate-[wave_8s_ease-in-out_infinite]'
              style={{
                top: `${40 + i * 25}px`,
                opacity: 0.15 - i * 0.04,
                transform: `scaleX(${1.2 + i * 0.1}) rotate(-3deg)`,
                animationDelay: `${i * 0.5}s`,
              }}
            />
          ))}
        </div>

        <div className='relative z-10 px-6 pt-12 pb-16 md:pt-16 md:pb-20 max-w-2xl mx-auto text-center'>
          <h1
            className='text-3xl md:text-4xl lg:text-5xl text-white leading-tight mb-4'
            style={{ fontFamily: "'Khula', sans-serif", fontWeight: 300 }}
          >
            Co-parenting
            <br />
            made peaceful
          </h1>
          <p className='text-white/85 text-base md:text-lg leading-relaxed mb-6 max-w-md mx-auto'>
            Keep everything documented. Stay organized.
            <br />
            Protect what matters.
          </p>

          {/* Trust Badge */}
          <div className='inline-flex items-center gap-2 bg-white/15 px-4 py-2 rounded-full'>
            <FiShield className='w-4 h-4 text-white' />
            <span className='text-white text-sm font-semibold'>
              Court-ready documentation
            </span>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className='px-6 py-8 max-w-2xl mx-auto -mt-8 relative z-20'>
        {/* Features Card */}
        <Card className='mb-6'>
          <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-4'>
            Everything you need
          </h2>

          <div className='space-y-4'>
            {FEATURES.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className='flex items-start gap-3'>
                  <div className='w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0'>
                    <Icon className='w-5 h-5 text-primary' />
                  </div>
                  <div>
                    <p className='font-bold text-gray-900 text-sm'>
                      {feature.text}
                    </p>
                    <p className='text-gray-500 text-sm'>
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Competitor Comparison */}
        <div className='flex items-center justify-center gap-2 bg-emerald-50 rounded-xl py-3 px-4 mb-6'>
          <div className='flex items-center gap-1 bg-white px-2.5 py-1 rounded-lg'>
            <FiTrendingDown className='w-4 h-4 text-emerald-600' />
            <span className='text-sm font-extrabold text-emerald-600'>
              70% less
            </span>
          </div>
          <span className='text-sm font-semibold text-emerald-700'>
            than OurFamilyWizard & TalkingParents
          </span>
        </div>

        {/* Pricing Section */}
        <div className='mb-6'>
          <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-4'>
            Choose your plan
          </h2>

          {sortedPlans.length > 0 ? (
            <div className='space-y-3'>
              {sortedPlans.map((plan) => {
                const info = getPlanInfo(plan);
                const isSelected = selectedPlan?.price_id === plan.price_id;

                return (
                  <button
                    key={plan.price_id}
                    onClick={() => setSelectedPlan(plan)}
                    className={`relative w-full text-left p-4 rounded-2xl border-2 transition-all ${
                      info.isAnnual
                        ? isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-amber-300/50 bg-amber-50/50'
                        : isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 bg-white/90'
                    }`}
                  >
                    {/* Best Value Badge */}
                    {info.isAnnual && (
                      <div className='absolute top-0 right-0 bg-amber-500 text-white text-[10px] font-extrabold uppercase tracking-wide px-3 py-1 rounded-bl-xl rounded-tr-xl'>
                        Best Value
                      </div>
                    )}

                    <div className='flex items-center'>
                      {/* Radio Button */}
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mr-3 ${
                          isSelected ? 'border-primary' : 'border-gray-300'
                        }`}
                      >
                        {isSelected && (
                          <div className='w-3 h-3 rounded-full bg-primary' />
                        )}
                      </div>

                      {/* Package Info */}
                      <div className='flex-1'>
                        <p
                          className={`font-bold ${
                            isSelected ? 'text-primary' : 'text-gray-900'
                          }`}
                        >
                          {info.title}
                        </p>
                        <p className='text-sm text-gray-500'>
                          {info.isAnnual
                            ? `${YEARLY_TRIAL_DAYS} days free, then ${info.monthlyEquivalent} billed annually`
                            : 'Billed monthly'}
                        </p>
                      </div>

                      {/* Price */}
                      <div className='text-right'>
                        <p
                          className={`text-xl font-extrabold ${
                            isSelected ? 'text-primary' : 'text-gray-900'
                          }`}
                        >
                          {info.priceString}
                        </p>
                        {info.savingsPercent > 0 && (
                          <div className='inline-block bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-md mt-1'>
                            Save {info.savingsPercent}%
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <Card className='text-center py-6'>
              <p className='text-gray-500'>
                No subscription options available. Please try again later.
              </p>
            </Card>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm text-center'>
            {error}
          </div>
        )}

        {/* CTA Section */}
        <div className='space-y-3 mb-6'>
          <Button
            fullWidth
            onClick={handlePurchase}
            disabled={isPurchasing || isRestoring || !selectedPlan}
            loading={isPurchasing}
            className='shadow-lg shadow-primary/30'
          >
            <FiShield className='w-5 h-5 mr-2' />
            {selectedPlan?.interval === 'year'
              ? 'Start 7-Day Free Trial'
              : 'Start Protecting My Records'}
          </Button>

          <button
            onClick={handleRestorePurchases}
            disabled={isPurchasing || isRestoring}
            className='w-full py-3 text-primary font-semibold hover:underline disabled:opacity-50'
          >
            {isRestoring ? (
              <span className='flex items-center justify-center gap-2'>
                <div className='animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full' />
                Restoring...
              </span>
            ) : (
              'Restore Purchases'
            )}
          </button>
        </div>

        {/* Terms */}
        <div className='text-center pb-8'>
          <p className='text-xs text-gray-500 leading-relaxed mb-3'>
            {selectedPlan?.interval === 'year'
              ? `7 days free, then ${
                  selectedPlan ? formatPrice(selectedPlan) : '$49.99'
                }/year. Subscription auto-renews unless canceled at least 24 hours before the trial ends. Cancel anytime in your account settings.`
              : 'Payment will be charged to your account, subscription auto-renews unless canceled 24 hours before period end.'}
          </p>
          <div className='flex items-center justify-center gap-2 text-xs'>
            <a
              href={TERMS_URL}
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary font-semibold hover:underline'
            >
              Terms of Use
            </a>
            <span className='text-gray-400'>|</span>
            <a
              href={PRIVACY_URL}
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary font-semibold hover:underline'
            >
              Privacy Policy
            </a>
          </div>
          {userEmail && (
            <p className='text-xs text-gray-500 mt-3'>
              Signed in as {userEmail}
            </p>
          )}
        </div>
      </div>

      {/* CSS for wave animation */}
      <style>{`
        @keyframes wave {
          0%, 100% { transform: translateY(0) scaleX(1.2) rotate(-3deg); }
          50% { transform: translateY(10px) scaleX(1.2) rotate(-3deg); }
        }
      `}</style>
    </div>
  );
}
